import { cn } from '../../../lib/utils';
import { NavigationItem } from './navigation-items';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { Link, useLocation } from 'react-router-dom';

interface NavItemProps {
  item: NavigationItem;
  collapsed: boolean;
}

export function NavItem({ item, collapsed }: NavItemProps) {
  const location = useLocation();
  const isActive = location.pathname === item.href;

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        <Link
          to={item.href}
          className={cn(
            "nav-item relative flex items-center w-full px-3 py-2.5 rounded-lg",
            "text-sm font-medium",
            "transition-all duration-300 ease-out",
            "hover:bg-primary/5 dark:hover:bg-primary/10",
            "group",
            collapsed ? "justify-center" : "justify-start",
            isActive && "bg-primary/10 dark:bg-primary/20"
          )}
        >
          {/* Active indicator */}
          {isActive && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-primary rounded-r-full" />
          )}
          
          <div className={cn(
            "flex items-center justify-center",
            "relative",
            "w-6 h-6",
            collapsed ? "" : "mr-3",
            "transition-transform duration-300 ease-out",
            "group-hover:scale-110"
          )}>
            <item.icon className={cn(
              "h-[18px] w-[18px]",
              "transition-colors duration-300",
              "text-muted-foreground group-hover:text-primary",
              isActive && "text-primary",
              item.color
            )} />
          </div>

          {!collapsed && (
            <span className={cn(
              "truncate transition-colors duration-300",
              isActive ? "text-primary" : "text-foreground/80",
              "group-hover:text-primary"
            )}>
              {item.name}
            </span>
          )}

          {!collapsed && item.badge && (
            <span className={cn(
              "ml-auto rounded-full px-2 py-0.5 text-xs font-medium",
              "transition-all duration-300",
              item.badge.variant === 'error' 
                ? "bg-destructive/15 text-destructive dark:bg-destructive/25"
                : "bg-primary/15 text-primary dark:bg-primary/25",
              "group-hover:bg-opacity-25"
            )}>
              {item.badge.content}
            </span>
          )}

          {/* Hover effect */}
          <div className={cn(
            "absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100",
            "transition-opacity duration-300",
            "pointer-events-none",
            "bg-gradient-to-r from-primary/5 to-transparent dark:from-primary/10"
          )} />
        </Link>
      </TooltipTrigger>
      {collapsed && (
        <TooltipContent 
          side="right" 
          className="flex items-center gap-2 bg-background/80 backdrop-blur-sm border border-border/50 shadow-lg"
        >
          <span className="font-medium">{item.name}</span>
          {item.badge && (
            <span className={cn(
              "rounded-full px-2 py-0.5 text-xs font-medium",
              item.badge.variant === 'error' 
                ? "bg-destructive/15 text-destructive dark:bg-destructive/25"
                : "bg-primary/15 text-primary dark:bg-primary/25"
            )}>
              {item.badge.content}
            </span>
          )}
        </TooltipContent>
      )}
    </Tooltip>
  );
}
